/** Dev Agent assistant markdown — Streamdown + artifact markers + Apex/data renderers. */

import { memo, useEffect, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";
import { MermaidScrollGuard } from "@/components/molecules/MermaidScrollGuard";
import { Streamdown } from "streamdown";
import { mermaid } from "@streamdown/mermaid";
import { code } from "@streamdown/code";
import "streamdown/styles.css";
import "../components/streamdownOverrides.css";
import { buildApexRenderer } from "../components/ApexCustomRenderer";
import { buildDataRenderer } from "../components/DataBlockRenderer";
import { fixMermaidUnquotedLabels } from "../components/mermaidScrub";
import { hasArtifactReference, rewriteArtifactMarkers } from "./artifactMarkers";
import { useDownloadAction } from "../download/useDownloadAction";
import { useDevAgentStore, type BackendArtifact } from "@/stores/devAgentStore";
import { useShallow } from "zustand/react/shallow";
import { normalizeAssistantContent } from "../normalizeAssistantContent";
import { prepareRenderableAssistantContent } from "../prepareRenderableAssistantContent";
import { getBackendArtifact } from "../devAgentService";

const BASE_PLUGINS = { mermaid, code };

const _EMPTY_ARTIFACTS: Record<string, BackendArtifact> = {};
const _ARTIFACT_ID_RE = /\bart_[a-f0-9]+\b/g;
const missingArtifactIds = new Set<string>();
const pendingArtifactIds = new Set<string>();

export interface DevAgentChatMessageContentProps {
  content: string;
  className?: string;
  showCopyButton?: boolean;
  isStreaming?: boolean;
  toolCalls?: unknown[];
  messageUuid?: string;
}

function DevAgentChatMessageContentInner({
  content,
  className = "",
  showCopyButton = true,
  isStreaming = false,
  toolCalls,
  messageUuid,
}: DevAgentChatMessageContentProps) {
  const normalizedContent = useMemo(() => normalizeAssistantContent(content), [content]);
  const renderableContent = useMemo(
    () => prepareRenderableAssistantContent(normalizedContent, { isStreaming }),
    [isStreaming, normalizedContent],
  );

  const { download: serverDownload, downloadingFormat } = useDownloadAction(
    toolCalls,
    normalizedContent,
    messageUuid,
  );

  const optsRef = useRef({ isStreaming, showCopyButton, serverDownload, downloadingFormat });
  optsRef.current = { isStreaming, showCopyButton, serverDownload, downloadingFormat };

  const plugins = useMemo(
    () => ({
      ...BASE_PLUGINS,
      renderers: [buildApexRenderer(optsRef), buildDataRenderer(optsRef)],
    }),
    [],
  );

  const hasMarker = hasArtifactReference(renderableContent);
  const { artifactsById, setArtifact } = useDevAgentStore(
    useShallow((s) => ({
      artifactsById: hasMarker ? s.artifactsById : _EMPTY_ARTIFACTS,
      setArtifact: s.setArtifact,
    }))
  );

  const referencedArtifactIds = useMemo(() => {
    if (!hasMarker) return [];
    return Array.from(new Set(renderableContent.match(_ARTIFACT_ID_RE) || []));
  }, [hasMarker, renderableContent]);

  useEffect(() => {
    if (!referencedArtifactIds.length) return;
    const missingIds = referencedArtifactIds.filter(
      (id) => !artifactsById[id]?.content && !missingArtifactIds.has(id) && !pendingArtifactIds.has(id),
    );
    if (!missingIds.length) return;

    let cancelled = false;
    void Promise.all(
      missingIds.map(async (id) => {
        pendingArtifactIds.add(id);
        try {
          const art = await getBackendArtifact(id);
          const isMissing = (art as BackendArtifact & { missing?: boolean })?.missing || !art?.content;
          if (isMissing) {
            missingArtifactIds.add(id);
          } else if (!cancelled && art?.id && typeof art.content === "string" && art.content.length > 0) {
            missingArtifactIds.delete(id);
            setArtifact(art);
          }
        } catch (err) {
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status === 404) {
            missingArtifactIds.add(id);
          }
          console.debug("[dev-agent artifact-inline-hydrate] failed", id, err);
        } finally {
          pendingArtifactIds.delete(id);
        }
      }),
    );

    return () => {
      cancelled = true;
    };
  }, [artifactsById, referencedArtifactIds, setArtifact]);

  const scrubbed = useMemo(
    () => fixMermaidUnquotedLabels(rewriteArtifactMarkers(renderableContent, artifactsById)),
    [renderableContent, artifactsById],
  );

  return (
    <div
      className={cn(
        "chat-streamdown",
        className?.trim() ? className : "text-foreground",
      )}
    >
      <MermaidScrollGuard>
        <Streamdown
          // Only while streaming: on settled text it appends `__` to an odd `__c` count (see SkillResultMarkdown).
          parseIncompleteMarkdown={isStreaming}
          plugins={plugins}
          controls={{ mermaid: false }}
          isAnimating={isStreaming}
          animated
        >
          {scrubbed}
        </Streamdown>
      </MermaidScrollGuard>
    </div>
  );
}

export const DevAgentChatMessageContent = memo(DevAgentChatMessageContentInner);
